import { Box, IconButton, List, ListItem } from "@mui/material";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useSubTasksStyles } from "./SubTasks.styles";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useTranslation } from "../../localization/useTranslation";
import { useSubTasksHook } from "./SubTasks.hooks";
import { DotLegalCheckbox, DotLegalCompletedPercentage, DotLegalHeader, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import AddIcon from "@mui/icons-material/Add";
import { isNullOrWhitespace } from "../../common/stringOperations";
import MentionInput from "../../common/components/mentionInput/MentionInput.tsx";
import { EntityType } from "../../common/Common.types.ts";
import DotLegalSection from "../../common/components/dotLegalSection/DotLegalSection.tsx";

export interface ISubTasksProps {
    taskId: string;
    updateTaskState: () => void;
    isAdHocTask: boolean;
    disabled: boolean;
    allDataIsDoneLoading: boolean;
    shouldAddTask?: boolean;
    onSuccessAddTask?: () => void;
    passIsDoneLoading?: (newValue: boolean) => void;
}

function SubTasks(props: ISubTasksProps) {
    const { translateString } = useTranslation();
    const {
        data,
        isLoading,
        onSubTaskChange,
        removeSubTask,
        hoveredSubTask,
        setHoveredSubTask,
        addSubTask,
        focusOnLastSubTask,
        lastSubTaskRef,
        updateDescription,
        handleDragEnd,
    } = useSubTasksHook(props);
    const styles = useSubTasksStyles(!isLoading && data?.subTasks !== undefined && data.subTasks!.length > 0);

    async function handleToggle(isComplete: boolean, id: string) {
        let tempData = { ...data! };

        const foundTask = tempData.subTasks.find((x) => x.id === id);
        foundTask!.isComplete = isComplete;

        await onSubTaskChange(tempData, foundTask!);
        props.updateTaskState();
    }

    async function removeTask(id: string) {
        await removeSubTask(id);

        if (!props.isAdHocTask) {
            props.updateTaskState();
        }
    }

    const handleMouseOver = (taskId: string) => {
        setHoveredSubTask(taskId);
    };

    const handleMouseOut = () => {
        setHoveredSubTask("");
    };

    return (
        <>
            {props.allDataIsDoneLoading && data?.subTasks !== undefined && data.subTasks!.length > 0 && (
                <DotLegalSection>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1 }}>
                        <DotLegalHeader marginBottom={0} marginTop={0} headerStyle="small" toolTip={translateString("SubTaskToolTip")}>
                            {translateString("subTasks")}
                        </DotLegalHeader>
                        {!props.disabled && (
                            <DotLegalTooltip text={translateString("addSubTask")}>
                                <IconButton
                                    sx={{ padding: 0.5 }}
                                    onClick={async () => {
                                        await addSubTask();
                                        if (!props.isAdHocTask) {
                                            props.updateTaskState();
                                        }
                                    }}
                                >
                                    <AddIcon />
                                </IconButton>
                            </DotLegalTooltip>
                        )}
                    </Box>

                    <DotLegalCompletedPercentage completed={data?.percentageComplate ?? 0} oneLiner isLoading={isLoading} />

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="subtasks">
                            {(provided) => (
                                <List ref={provided.innerRef} {...provided.droppableProps} sx={styles.root}>
                                    {!isLoading &&
                                        data?.subTasks?.map((subTask, index) => {
                                            const labelId = `checkbox-list-label-${index}`;
                                            const isLastSubTask = data!.subTasks[data!.subTasks.length - 1].id === subTask.id;
                                            return (
                                                <Draggable key={subTask.id} draggableId={subTask.id} index={index} isDragDisabled={props.disabled}>
                                                    {(provided, snapshot) => (
                                                        <Box
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            onMouseEnter={() => handleMouseOver(subTask.id)}
                                                            onMouseLeave={handleMouseOut}
                                                            sx={(theme) => ({
                                                                ...(snapshot.isDragging && {
                                                                    background: theme.palette.primary.light,
                                                                    borderRadius: 1,
                                                                }),
                                                                marginLeft: "-6px",
                                                            })}
                                                        >
                                                            <ListItem sx={styles.listItem} role={undefined} dense>
                                                                {!props.disabled && (
                                                                    <Box
                                                                        {...provided.dragHandleProps}
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignSelf: "flex-start",
                                                                            marginTop: 0.3,
                                                                            visibility: hoveredSubTask === subTask.id ? "visible" : "hidden",
                                                                        }}
                                                                    >
                                                                        <DragIndicatorIcon
                                                                            sx={(theme) => ({
                                                                                marginTop: "-2px",
                                                                                fill: theme.palette.grey[500],
                                                                                height: 20,
                                                                                outline: "none",
                                                                                "&:hover": {
                                                                                    cursor: "grab",
                                                                                },
                                                                            })}
                                                                        />
                                                                    </Box>
                                                                )}
                                                                <Box sx={{ alignSelf: "flex-start" }}>
                                                                    <DotLegalCheckbox
                                                                        disabled={props.disabled || isNullOrWhitespace(subTask.description)}
                                                                        key={labelId}
                                                                        onChange={async (e) => {
                                                                            await handleToggle(e, subTask.id);
                                                                        }}
                                                                        checked={subTask.isComplete}
                                                                    />
                                                                </Box>

                                                                <Box sx={[styles.subTaskDescription]} ref={isLastSubTask ? lastSubTaskRef : undefined}>
                                                                    <MentionInput
                                                                        mentionSource={{ entityFromId: props.taskId, entityFromType: EntityType.Task }}
                                                                        disabled={props.disabled}
                                                                        noMargin
                                                                        variant="standard"
                                                                        debounce
                                                                        multiline
                                                                        placeholder={translateString("subTaskPlaceHolder")}
                                                                        value={subTask.description}
                                                                        onChange={async (value) => updateDescription(value, subTask.id)}
                                                                        alwaysShowUnderline
                                                                        textDecoration={subTask.isComplete ? "line-through" : undefined}
                                                                        initialTextFieldFocus={focusOnLastSubTask && isLastSubTask}
                                                                    />
                                                                </Box>

                                                                {!props.disabled && (
                                                                    <IconButton
                                                                        disabled={props.disabled}
                                                                        sx={{
                                                                            alignSelf: "flex-start",
                                                                            visibility: hoveredSubTask === subTask.id ? "visible" : "hidden",
                                                                        }}
                                                                        size="small"
                                                                        onClick={async () => await removeTask(subTask.id)}
                                                                    >
                                                                        <CloseIcon sx={styles.remove} />
                                                                    </IconButton>
                                                                )}
                                                            </ListItem>
                                                        </Box>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                    {provided.placeholder}
                                </List>
                            )}
                        </Droppable>
                    </DragDropContext>
                </DotLegalSection>
            )}
        </>
    );
}

export default SubTasks;
